import { nullish } from "helpers/nullish";
import { ArrayQueryKey } from "react-query";
import { ICollectionRef, IRollupSnapshotResponse } from "../types/server-rollups";

export const SPACES = "SPACES";
export const MERCHANTS = "MERCHANTS";
export const BRANDS = "BRANDS";
export const SERVER_ROLLUPS = "SERVER_ROLLUPS";
export const COLLECTIONS = "COLLECTIONS";

export const ROOT_ITEM = "root";

export enum SpaceView {
    MerchantDrive = "merchantDrive",
    MyDrive = "myDrive",
    SharedWithMe = "sharedWithMe",
    MerchantDriveTrash = "merchantDriveTrash",
    MyDriveTrash = "myDriveTrash",
    Starred = "starred",
}

const validViews = [
    SpaceView.MyDrive,
    SpaceView.MyDriveTrash,
    SpaceView.MerchantDrive,
    SpaceView.MerchantDriveTrash,
    SpaceView.SharedWithMe,
    SpaceView.Starred,
];

export const isTrashView = (view) => [SpaceView.MerchantDriveTrash, SpaceView.MyDriveTrash].includes(view);

export const isStarredView = (view) => SpaceView.Starred === view;

export const isSharedWithMeView = (view) => SpaceView.SharedWithMe === view;

export const sanitizeView = (view: SpaceView | string) => {
    const indexView = validViews.findIndex((it) => view && it?.toLowerCase() === view?.toLowerCase());
    return indexView >= 0 ? validViews[indexView] : null;
};

export const shouldRetryHookOnError = (error) => error?.code < 400 || error?.code > 499;
export const shouldRefreshCurrentDriveAssets = (error) => error?.code === 400 && error?.serviceErrorCode === 9;

export const removeAssortmentExtRegex = /^(.*)\.assort$/i;

export const removeWhiteboardExtRegex = /^(.*)\.board$/i;

export const removeShortcutExtRegex = /^(.*)\.short$/i;

export const removeRollupExtRegex = /^(.*)\.roll$/i;

interface IAdditionalKeys {
    isParentSpace?: boolean;
    isLineage?: boolean;
    isSpaceList?: boolean;
    spaceView?: SpaceView;
    search?: string;
    isPermissions?: boolean;
    isFolderSelection?: boolean;
    isExternalCall?: boolean;
    starredItems?: boolean;
    params?: any;
}

export interface IReadSnapshotFilters {
    doorDimensionId: string;
    deliveryDimensionId: string;
    sizeDimensionId: string;
    doorId?: string;
    productId?: string;
    collectionId?: string;
}

interface IAdditionalServerRollupKeys {
    withSnapshot?: boolean;
    readSnapshot?: boolean;
    readSnapshotItems?: boolean;
    filters?: IReadSnapshotFilters | any;
}

export const isListViewKey = (queryKey: ArrayQueryKey) => {
    const [rootKey, others] = queryKey;
    if (rootKey !== SPACES) {
        return false;
    }

    const { merchantId, itemId, memberId, additionalKeys } = (others as any) || {};
    const driveOrShared = (itemId && !memberId) || (!itemId && memberId); // in drives itemId is defined and in root of shared memberId is defined
    return (
        merchantId &&
        driveOrShared &&
        !additionalKeys.isFolderSelection &&
        Object.keys(additionalKeys?.params || {}).some((key) =>
            ["filter", "order_by", "order_by_direction"].includes(key),
        )
    );
};

export const isRollupContainingTrashedItem = (collectionSnapshot: IRollupSnapshotResponse) => {
    let hasTrashedItem = false;
    collectionSnapshot?.collection_refs?.some((collection_ref: ICollectionRef) => {
        if (collection_ref?.collection_trashed_status === true) {
            hasTrashedItem = true;
            return true;
        }
    });
    return hasTrashedItem;
};

export const buildSpaceCacheKeys = (
    merchantId: string,
    itemId?: string,
    memberId?: string,
    additionalKeys: IAdditionalKeys = { isParentSpace: false },
) => {
    const compositeIds = { merchantId, itemId: undefined, memberId: undefined, additionalKeys: undefined };

    if (itemId) {
        compositeIds.itemId = itemId;
    }

    if (memberId) {
        compositeIds.memberId = memberId;
    }

    if (additionalKeys && Object.keys(additionalKeys).length > 0) {
        compositeIds.additionalKeys = additionalKeys;
    }

    return [SPACES, compositeIds];
};

export const buildCollectionCacheKeys = (compositeIds: {
    merchantId?: string;
    collectionId?: string;
    isCopying?: boolean;
}) => {
    return [COLLECTIONS, compositeIds];
};

export const buildMerchantCacheKeys = (userId: string, merchantId: string, brandId?: string) => {
    const compositeIds = { userId, merchantId, brandId };

    return [MERCHANTS, compositeIds];
};

export const buildMerchantsSharingWithMeCacheKeys = (merchantId: string, memberId: string) => {
    const compositeIds = { merchantId, memberId };

    return [MERCHANTS, compositeIds];
};

export const buildBrandCacheKeys = (merchantId?: string, isExternalCall?: boolean) => {
    const compositeIds = { merchantId: undefined, isExternalCall: undefined };
    if (merchantId) {
        compositeIds.merchantId = merchantId;
    }

    if (!nullish(isExternalCall)) {
        compositeIds.isExternalCall = isExternalCall;
    }

    return [BRANDS, compositeIds];
};

export const buildSearchHashtagsCacheKeys = (snapshotId?: string, merchantId?: string, hashtagSearchText?: string) => {
    const compositeIds = { merchantId: undefined, hashtagSearchText: undefined, snapshotId: undefined };

    if (merchantId) {
        compositeIds.merchantId = merchantId;
    }

    if (hashtagSearchText) {
        compositeIds.hashtagSearchText = hashtagSearchText;
    }

    if (snapshotId) {
        compositeIds.snapshotId = snapshotId;
    }

    return [compositeIds];
};

export const buildServerRollupCacheKeys = (
    merchantId: string,
    rollupItemId?: string,
    snapshotId?: string,
    { withSnapshot, readSnapshot, readSnapshotItems, filters }: IAdditionalServerRollupKeys = {},
) => {
    const compositeIds = {
        merchantId: undefined,
        snapshotId: undefined,
        rollupItemId: undefined,
        withSnapshot: undefined,
        readSnapshot: undefined,
        readSnapshotItems: undefined,
        filters: undefined,
    };
    if (merchantId) {
        compositeIds.merchantId = merchantId;
    }

    if (!nullish(rollupItemId)) {
        compositeIds.rollupItemId = rollupItemId;
    }

    if (!nullish(snapshotId)) {
        compositeIds.snapshotId = snapshotId;
    }

    if (!nullish(withSnapshot)) {
        compositeIds.withSnapshot = withSnapshot;
    }

    if (!nullish(readSnapshot)) {
        compositeIds.readSnapshot = readSnapshot;
    }

    if (!nullish(readSnapshotItems)) {
        compositeIds.readSnapshotItems = readSnapshotItems;
    }

    if (filters && Object.keys(filters).length > 0) {
        compositeIds.filters = filters;
    }

    return [SERVER_ROLLUPS, compositeIds];
};

export const flattenInfiniteData = <TResponse, TItem>(
    responses: TResponse[] = [],
    extractFn: (response: TResponse) => TItem[],
): TItem[] => {
    return responses.reduce((flat, toFlatten) => {
        return flat.concat(extractFn(toFlatten));
    }, []);
};

export const fetchMoreDelayed = (
    canFetchMore: boolean,
    isFetchingMore: boolean | string,
    isFetching: boolean,
    fetchMore: () => void,
) => {
    if (canFetchMore && !isFetchingMore && !isFetching) {
        process.nextTick(() => fetchMore());
    }
};

export const infiniteAction = <TResponse, TItem>(
    responses: TResponse[] = [],
    attributeName: string,
    findIndexFn: (items: TItem[], id: string) => number,
    compareId: string,
    actionFn: (items: TItem[], index: number, data?: TItem) => void,
    newData?: TItem,
): TResponse[] => {
    let wasRemoved = false;
    return responses.reduce((acc, response) => {
        if (!wasRemoved) {
            const newItems = [...response[attributeName]];
            const deletedIndex = findIndexFn(newItems, compareId);
            if (deletedIndex >= 0) {
                wasRemoved = true;
                actionFn(newItems, deletedIndex, newData);
                acc.push({
                    ...response,
                    [attributeName]: newItems,
                });
                return acc;
            }
        }
        acc.push(response);
        return acc;
    }, []);
};

export const deleteItemInResponse = <TResponse, TItem>(
    responses: TResponse[] = [],
    attributeName: string,
    findIndexFn: (items: TItem[], id: string) => number,
    compareId: string,
    actionFn: (items: TItem[], index: number) => void,
): TResponse[] => {
    return infiniteAction(responses, attributeName, findIndexFn, compareId, actionFn);
};

export const replaceItemInResponse = <TResponse, TItem>(
    responses: TResponse[] = [],
    attributeName: string,
    findIndexFn: (items: TItem[], id: string) => number,
    compareId: string,
    actionFn: (items: TItem[], index: number, data: TItem) => void,
    newData: TItem,
): TResponse[] => {
    return infiniteAction(responses, attributeName, findIndexFn, compareId, actionFn, newData);
};

export const getDriveCacheKey = (
    view: SpaceView,
    merchantId: string,
    itemId: string,
    rootItemId: string,
    memberId: string,
    params: any,
) => {
    const merchantDriveKey = buildSpaceCacheKeys(merchantId, itemId, undefined, { params });
    const shareWithMeKey = buildSpaceCacheKeys(merchantId, undefined, memberId, { params });
    const starredDriveKey = buildSpaceCacheKeys(merchantId, undefined, undefined, { params, starredItems: true });
    if (rootItemId && rootItemId !== itemId) {
        return merchantDriveKey;
    }

    switch (view) {
        case SpaceView.SharedWithMe:
            return shareWithMeKey;
        case SpaceView.MerchantDrive:
            return merchantDriveKey;
        case SpaceView.Starred:
            return starredDriveKey;
        default:
            return merchantDriveKey;
    }
};
